import { useEffect, useRef, useCallback } from 'react'

export const useEMDRAnimation = ({
  isRunning,
  frequency,
  verticalPosition,
  ballSize = 1,
  onDirectionChange
}) => {
  const ballRef = useRef(null)
  const animationRef = useRef(null)
  const stateRef = useRef({
    position: 0,
    direction: 1,
    phase: 0,
    amplitude: 0,
    lastFrameTime: 0,
    returningToCenter: false,
    velocity: 0
  })

  const calculateAmplitude = useCallback(() => {
    if (!ballRef.current) return 0
    const containerWidth = ballRef.current.parentElement?.clientWidth || window.innerWidth
    return Math.max(100, (containerWidth / 2) - 50)
  }, [])

  const animate = useCallback((timestamp) => {
    if (!ballRef.current) return

    const state = stateRef.current
    const deltaTime = timestamp - state.lastFrameTime
    
    if (deltaTime < 16) { // Limite à ~60fps
      animationRef.current = requestAnimationFrame(animate)
      return
    }

    state.lastFrameTime = timestamp
    state.amplitude = calculateAmplitude()

    if (!isRunning && state.position !== 0) {
      // Animation de retour au centre en douceur
      state.returningToCenter = true
      const returnSpeed = 0.05 // Vitesse de retour (plus petit = plus lent)
      state.position = state.position * (1 - returnSpeed)
      
      // Arrêter l'animation si très proche du centre
      if (Math.abs(state.position) < 0.5) {
        state.position = 0
        state.returningToCenter = false
        state.phase = 0
        
        // Application finale de la transformation
        ballRef.current.style.transform = 
          `translate(0px, ${verticalPosition}px) scale(${ballSize})`
        return // Arrêter l'animation
      }
    } else if (isRunning) {
      // Animation normale EMDR
      state.returningToCenter = false
      
      // Calcul de la position basé sur une sinusoïde
      const period = 1000 / frequency // période en ms
      state.phase += (deltaTime / period) * 2 * Math.PI
      
      const newPosition = Math.sin(state.phase) * state.amplitude
      const newDirection = Math.cos(state.phase) > 0 ? 1 : -1

      // Détection du changement de direction
      if (newDirection !== state.direction) {
        state.direction = newDirection
        onDirectionChange?.(newDirection)
      }

      state.position = newPosition
    }

    // Application de la transformation
    ballRef.current.style.transform = 
      `translate(${Math.round(state.position)}px, ${verticalPosition}px) scale(${ballSize})`

    if (isRunning || state.returningToCenter) {
      animationRef.current = requestAnimationFrame(animate)
    }
  }, [isRunning, frequency, verticalPosition, ballSize, onDirectionChange, calculateAmplitude])

  useEffect(() => {
    if (isRunning) {
      stateRef.current.lastFrameTime = performance.now()
      stateRef.current.returningToCenter = false
      animationRef.current = requestAnimationFrame(animate)
    } else {
      // Déclencher l'animation de retour au centre si pas déjà au centre
      if (stateRef.current.position !== 0) {
        stateRef.current.lastFrameTime = performance.now()
        animationRef.current = requestAnimationFrame(animate)
      } else if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isRunning, animate])

  // Reset ball position when needed
  const resetBallPosition = useCallback(() => {
    if (ballRef.current) {
      stateRef.current.position = 0
      stateRef.current.phase = 0
      stateRef.current.returningToCenter = false
      ballRef.current.style.transform = `translate(0px, ${verticalPosition}px) scale(${ballSize})`
    }
  }, [verticalPosition, ballSize])

  // Mise à jour immédiate de la position verticale
  useEffect(() => {
    if (ballRef.current && !isRunning && !stateRef.current.returningToCenter) {
      ballRef.current.style.transform = `translate(${Math.round(stateRef.current.position)}px, ${verticalPosition}px) scale(${ballSize})`
    }
  }, [verticalPosition, isRunning, ballSize])

  return { ballRef, resetBallPosition }
} 